import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";

export const useFetchMatchProfile = () => {
  const { fetchData } = useApi();
  const { organizationId, sport, matchProfileId } = useParams();

  return useQuery({
    queryKey: ["matchProfiles", organizationId, matchProfileId],
    queryFn: async () => {
      const result = await fetchData({
        url: `${sport}/o/${organizationId}/fixtureProfiles/${matchProfileId}`,
      });
      return result?.data?.[0];
    },
    enabled: !!matchProfileId,
  });
};

export const useFetchMatchProfilesPagination = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/fixtureProfiles`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["matchProfiles", organizationId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useFetchMatchProfiles = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport } = useParams();
  let URL = `${sport}/o/${organizationId}/fixtureProfiles`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["matchProfiles", organizationId, JSON.stringify(params)],
    queryFn: async () => {
      const result = await fetchData({
        url: URL,
      });
      return result?.data || [];
    },
  });
};

export const useDeleteMatchProfile = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (profileId) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/fixtureProfiles/${profileId}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      notification.success({
        message: "Match profile deleted successfully",
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["matchProfiles"] });
    },
  });
};

export const useCreateMatchProfile = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/fixtureProfiles`,
        method: "POST",
        data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "match.profile.add.success",
          defaultMessage: "Match Profile added successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["matchProfiles"] });
    },
  });
};

export const useUpdateMatchProfile = () => {
  const { sport, organizationId, matchProfileId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/fixtureProfiles/${matchProfileId}`,
        method: "PUT",
        data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "match.profile.update.success",
          defaultMessage: "Match Profile updated successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["matchProfiles"] });
    },
  });
};
